/**
 * The required action algorithm.
 *
 * `contracts/typescript/game-sdk.ts` declares `RequiredActionAlgorithm` as an
 * ordered nine-step tuple and says implementations must preserve those steps in
 * that order. This module is that implementation, isolated from the transport so
 * it can be tested exhaustively without a socket, a DOM, or a clock.
 *
 * The order carries the acceptance criteria:
 *
 *   VERIFY_SESSION_AND_MESSAGE_SCHEMA        LIVE-006 (and no field is read
 *                                            off an unvalidated frame)
 *   REJECT_IF_EXPIRES_AT_IS_PAST             LIVE-005 — before the dedupe check,
 *                                            so an expired duplicate is EXPIRED
 *                                            rather than DUPLICATE, and before
 *                                            the handler, so it cannot run
 *   RETURN_DUPLICATE_ACK_IF_ALREADY_APPLIED  LIVE-004 — acknowledged, not
 *                                            re-executed
 *   LOOK_UP_REGISTERED_ACTION                an unregistered key is UNKNOWN_ACTION
 *   VALIDATE_PAYLOAD_AGAINST_MANIFEST_SCHEMA LIVE-006 — INVALID_PAYLOAD
 *   APPLY_LOCAL_RATE_AND_GAME_STATE_GUARDS   the game's own back pressure
 *   EXECUTE_HANDLER_WITH_ABORT_SIGNAL
 *   PERSIST_ACTION_ID_IN_BOUNDED_DEDUPE_SET  after success only
 *   SEND_ACK                                 always, including on every failure
 *
 * Every branch produces an ACK. A silently dropped Action is indistinguishable
 * from a lost one, and the server would keep retrying it until its TTL.
 */
import { AppliedActionSet } from './dedupe.js';
/**
 * The five outcomes, as values rather than only as a type.
 *
 * A union type vanishes at run time, and the documentation shipped inside a
 * vendored copy of this SDK has to enumerate these — a hand-written list of
 * four of the five is not a shorter document, it is a wrong one, and the reader
 * has no way to tell. `scripts/game-sdk-reference.mjs` reads these arrays, so
 * the generated reference cannot fall behind the code.
 */
export const ACTION_ACK_STATUSES = [
    'APPLIED',
    'REJECTED',
    'EXPIRED',
    'FAILED',
    'DUPLICATE',
];
export const ACTION_ACK_REASONS = [
    'UNKNOWN_ACTION',
    'INVALID_PAYLOAD',
    'GAME_STATE_BLOCKED',
    'LOCAL_RATE_LIMIT',
    'TTL_EXPIRED',
    'INTERNAL_ERROR',
];
export class ActionProcessor {
    applied;
    actions = new Map();
    guards = [];
    inFlight = new Map();
    /** Timeout is ambiguous: a handler that ignores abort may still finish later. */
    timedOut = new Map();
    options;
    paused = null;
    clientSequence = 0;
    constructor(options) {
        this.applied = new AppliedActionSet(options.dedupeCapacity);
        this.options = {
            gameSessionId: options.gameSessionId,
            sdkVersion: options.sdkVersion,
            newAckId: options.newAckId,
            now: options.now ?? (() => new Date()),
            dedupeCapacity: options.dedupeCapacity ?? 512,
            clockSkewToleranceMs: options.clockSkewToleranceMs ?? 2_000,
            handlerTimeoutMs: options.handlerTimeoutMs ?? 2_000,
        };
    }
    register(action) {
        if (this.actions.has(action.actionKey)) {
            throw new Error(`action '${action.actionKey}' is already registered`);
        }
        this.actions.set(action.actionKey, action);
        return () => {
            this.actions.delete(action.actionKey);
        };
    }
    addGuard(guard) {
        this.guards.push(guard);
        return () => {
            const index = this.guards.indexOf(guard);
            if (index >= 0)
                this.guards.splice(index, 1);
        };
    }
    /** Creator- or game-initiated pause. Actions are refused, never buffered. */
    pause(reason) {
        this.paused = reason;
    }
    resume() {
        this.paused = null;
    }
    get registeredKeys() {
        return [...this.actions.keys()];
    }
    get appliedCount() {
        return this.applied.size;
    }
    ack(envelope, status, reasonCode, detail, appliedAt) {
        this.clientSequence += 1;
        return {
            schemaVersion: 1,
            messageType: 'ACTION_ACK',
            ackId: this.options.newAckId(),
            actionId: envelope.actionId,
            gameSessionId: this.options.gameSessionId,
            status,
            reasonCode,
            detail: detail === null ? null : detail.slice(0, 300),
            appliedAt: appliedAt === null ? null : appliedAt.toISOString(),
            receivedAt: this.options.now().toISOString(),
            clientSequence: this.clientSequence,
            sdkVersion: this.options.sdkVersion,
        };
    }
    /**
     * Process one already-schema-valid envelope.
     *
     * The caller validates the frame against `action-envelope.schema.json` and
     * checks the session binding before calling — step 1 of the algorithm belongs
     * to the transport because that is where a frame that fails it must close the
     * socket rather than produce an ACK for an action id it cannot trust.
     */
    async process(envelope) {
        const now = this.options.now();
        // Step 2. Before dedupe and before the handler. An Action whose TTL has
        // passed is never applied — not on first delivery, not on redelivery, and
        // above all not after a reconnect (LIVE-005).
        const expiresAtMs = Date.parse(envelope.expiresAt);
        if (Number.isNaN(expiresAtMs)) {
            return this.ack(envelope, 'REJECTED', 'INVALID_PAYLOAD', 'expiresAt is not a timestamp', null);
        }
        if (expiresAtMs + this.options.clockSkewToleranceMs <= now.getTime()) {
            return this.ack(envelope, 'EXPIRED', 'TTL_EXPIRED', 'action expired before it was applied', null);
        }
        // Step 3. A duplicate is acknowledged without repeating the effect.
        if (this.applied.has(envelope.actionId)) {
            return this.ack(envelope, 'DUPLICATE', null, 'already applied on this client', null);
        }
        // A retry can arrive before step 8 records success. Join the original work
        // without running its handler twice; each delivery still gets a fresh ACK.
        const running = this.inFlight.get(envelope.actionId);
        if (running !== undefined) {
            const outcome = await running;
            return outcome.status === 'APPLIED'
                ? this.ack(envelope, 'DUPLICATE', null, 'already applied on this client', null)
                : this.ack(envelope, outcome.status, outcome.reasonCode, outcome.detail, null);
        }
        for (const [actionId, previous] of this.timedOut) {
            if (previous.expiresAtMs + this.options.clockSkewToleranceMs <= now.getTime())
                this.timedOut.delete(actionId);
        }
        const previous = this.timedOut.get(envelope.actionId);
        if (previous !== undefined) {
            return this.ack(envelope, previous.outcome.status, previous.outcome.reasonCode, previous.outcome.detail, null);
        }
        if (this.paused !== null) {
            return this.ack(envelope, 'REJECTED', 'GAME_STATE_BLOCKED', this.paused, null);
        }
        // Step 4.
        const action = this.actions.get(envelope.actionKey);
        if (action === undefined) {
            return this.ack(envelope, 'REJECTED', 'UNKNOWN_ACTION', `no handler registered for '${envelope.actionKey}'`, null);
        }
        // Step 5. LIVE-006: the payload must satisfy the schema the release manifest
        // declared, checked on the client too and not only on the server.
        let payloadValid;
        try {
            payloadValid = action.validatePayload(envelope.payload);
        }
        catch {
            payloadValid = false;
        }
        if (!payloadValid) {
            return this.ack(envelope, 'REJECTED', 'INVALID_PAYLOAD', 'payload does not satisfy the declared action schema', null);
        }
        // Step 6.
        for (const guard of this.guards) {
            const verdict = guard(envelope);
            if (!verdict.allow) {
                return this.ack(envelope, 'REJECTED', verdict.reason, verdict.detail ?? null, null);
            }
        }
        if (this.inFlight.size + this.timedOut.size >= this.options.dedupeCapacity) {
            return this.ack(envelope, 'REJECTED', 'LOCAL_RATE_LIMIT', 'pending action capacity reached', null);
        }
        const outcome = await this.executeHandler(action, envelope, now, expiresAtMs);
        return this.ack(envelope, outcome.status, outcome.reasonCode, outcome.detail, outcome.appliedAt);
    }
    /** Publish the shared promise before calling game code, including reentrant handlers. */
    executeHandler(action, envelope, now, expiresAtMs) {
        let resolve;
        const result = new Promise((finish) => {
            resolve = finish;
        });
        this.inFlight.set(envelope.actionId, result);
        // Abort alone cannot settle a promise returned by a handler that ignores
        // its signal. Bound the ACK wait separately and retain only a small failure
        // record until TTL, rather than retaining an unresolvable in-flight entry.
        const controller = new AbortController();
        const budgetMs = Math.max(0, Math.min(expiresAtMs - now.getTime(), this.options.handlerTimeoutMs));
        const deadline = performance.now() + budgetMs;
        let settled = false;
        const finish = (outcome, timeout) => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            this.inFlight.delete(envelope.actionId);
            if (timeout)
                this.timedOut.set(envelope.actionId, { expiresAtMs, outcome });
            if (outcome.status === 'APPLIED')
                this.applied.add(envelope.actionId, outcome.appliedAt.getTime());
            resolve(outcome);
        };
        const timeout = () => {
            controller.abort();
            const expired = this.options.now().getTime() >= expiresAtMs;
            finish({
                status: expired ? 'EXPIRED' : 'FAILED',
                reasonCode: expired ? 'TTL_EXPIRED' : 'GAME_STATE_BLOCKED',
                detail: expired ? 'action expired during its handler' : 'handler exceeded its budget',
                appliedAt: null,
            }, true);
        };
        // Kept alive until an owed ACK settles; always cleared on every outcome.
        const timer = setTimeout(timeout, budgetMs);
        void Promise.resolve()
            .then(() => {
            if (settled)
                return;
            if (controller.signal.aborted ||
                performance.now() >= deadline ||
                this.options.now().getTime() >= expiresAtMs) {
                timeout();
                return;
            }
            if (this.paused !== null) {
                finish({
                    status: 'REJECTED',
                    reasonCode: 'GAME_STATE_BLOCKED',
                    detail: this.paused,
                    appliedAt: null,
                }, false);
                return;
            }
            return action.handle(envelope.payload, envelope, {
                now: now.toISOString(),
                isReplayAttempt: envelope.attempt > 1,
                abortSignal: controller.signal,
            });
        })
            .then(() => {
            if (settled)
                return;
            if (controller.signal.aborted ||
                performance.now() >= deadline ||
                this.options.now().getTime() >= expiresAtMs) {
                timeout();
                return;
            }
            finish({ status: 'APPLIED', reasonCode: null, detail: null, appliedAt: this.options.now() }, false);
        }, (error) => {
            if (settled)
                return;
            if (controller.signal.aborted ||
                performance.now() >= deadline ||
                this.options.now().getTime() >= expiresAtMs) {
                timeout();
                return;
            }
            finish({
                status: 'FAILED',
                reasonCode: 'INTERNAL_ERROR',
                detail: error instanceof Error ? error.message : 'handler failed',
                appliedAt: null,
            }, false);
        });
        return result;
    }
}
